import { useState } from "react";

import { useAnalyticsEvents } from "@atlaskit/analytics-next";

import { useBxpWebStargateClient } from "@/libraries/bxp-web-stargate-client/src/hook/use-bxp-web-stargate-client";
import { FetchProjectRolesResponse } from "@/libraries/bxp-web-stargate-client/src/hook/use-bxp-web-stargate-client.types";

export type ProjectRoleIds = {
    viewer: number | null;
    member: number | null;
    admin: number | null;
};

const EMPTY = { viewer: null, member: null, admin: null };

export const useJfind1663FetchProjectRoleIds = () => {
    const stargateClient = useBxpWebStargateClient();
    const { createAnalyticsEvent } = useAnalyticsEvents();

    const [hasFetched, setHasFetched] = useState(false);
    const [result, setResult] = useState<ProjectRoleIds>(EMPTY);

    const fetchRoles = async (
        cloudId: string,
        projectId: string
    ): Promise<ProjectRoleIds> => {
        if (!cloudId || !projectId) {
            return Promise.resolve(EMPTY);
        }

        if (hasFetched) {
            return Promise.resolve(result);
        }

        try {
            const roles = await stargateClient.fetchProjectRoles(
                cloudId,
                projectId
            );

            const viewer = findRoleIdWithName(["viewer"], roles);
            const member = findRoleIdWithName(["member", "user"], roles);
            const admin = findRoleIdWithName(["administrator"], roles);

            setResult({ viewer, member, admin });
            setHasFetched(true);

            return Promise.resolve({ viewer, member, admin });
        } catch (error) {
            createAnalyticsEvent({
                eventType: "track",
                action: "failed_to_fetch_project_roles",
                actionSubject: "jfind_1663_onboarding_wizard",
                attributes: {
                    cloudId,
                    projectId,
                    error: (error as Error).message
                }
            });
            setHasFetched(true); // only attempt once
        }

        return Promise.resolve(EMPTY);
    };

    return { fetchRoles };
};

export const findRoleIdWithName = (
    matching: string[],
    roles: FetchProjectRolesResponse
) => {
    const role = Object.keys(roles).find((role) =>
        matching.includes(role.toLowerCase())
    );

    if (!role) {
        return null;
    }

    // formatted '.../rest/api/3/project/projectId/ role/ projectRoleId'
    const id = roles[role]?.split("role/").at(1);
    return id ? Number(id) : null;
};
